/* REACT PACKAGE IMPORTS */
import { StrictMode, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { BrowserRouter, Routes, Route } from "react-router-dom";

/* INTERNAL IMPORTS */
import App from "./App";
import MSALApp from "./MSALApp";
import { store } from "./App/store";
import ThemeContextWrapper from "./Theme/ThemeWrapper";
import reportWebVitals from "./reportWebVitals";
import "./styles/index.css";

/* ENTRA IMPORTS */
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";

/* ============= MSAL Setup ============= */
/**
 * MSAL is instantiated outside the component tree, prevents it from being re-instantiated on re-renders. MSAL instance requires the msal provider to be set up
 */
const msalInstance = new PublicClientApplication(msalConfig);

// Default to using the first account if no account is active on page load
if (
  !msalInstance.getActiveAccount() &&
  msalInstance.getAllAccounts().length > 0
) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  msalInstance.setActiveAccount(msalInstance.getActiveAccount()[0]);
}

// Listen for sign-in event and set active account
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
    const account = event.payload.account;
    msalInstance.setActiveAccount(account);
  }
});

/* ============= AppWrapper Setup ============= */

const AppWrapper = () => {
  const [authToken, setAuthToken] = useState(false);
  const ALLOWED_ORIGINS =
    process.env.REACT_APP_ALLOWED_ORIGINS?.split(",") ?? [];
  const isEmbeddedIframe =
    window && window.parent && window.location !== window.parent.location;

  useEffect(() => {
    const message = {
      message: "VX Frontend has requested to start assessment",
    };
    ALLOWED_ORIGINS.forEach((origin_url) =>
      window.parent.postMessage(message, origin_url),
    );

    const messageHandler = (event) => {
      const { data } = event;

      const isFromTrustedOrigin = ALLOWED_ORIGINS.includes(event.origin);

      if (!isFromTrustedOrigin) return;
      sessionStorage.setItem("post-message-data", data);

      if (data.message === "Start Child Window Assessment") {
        setAuthToken(data.AUTH_TOKEN); // Update state
      }
    };

    window.addEventListener("message", messageHandler);
    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, [ALLOWED_ORIGINS]);

  return (
    <StrictMode>
      <ThemeContextWrapper>
        <BrowserRouter>
          <Provider store={store}>
            <Routes>
              <Route
                path="/*"
                element={
                  (isEmbeddedIframe && authToken) ? (
                    <App authToken={authToken} />
                  ) : (
                    <MSALApp instance={msalInstance} />
                  )
                }
              />
            </Routes>
          </Provider>
        </BrowserRouter>
      </ThemeContextWrapper>
    </StrictMode>
  );
};

// Render the AppWrapper component
ReactDOM.render(<AppWrapper />, document.getElementById("root"));

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
